import { useAtomValue, useSetAtom } from 'jotai';

import {
  TreatmentPlanStatus,
  TreatmentPlansOverviewConnected,
  useShouldPromptForCSSRS,
} from '@grow-therapy-team/seedling-components';

import { EventTypeEnum } from '../../../segment/types';
import {
  ProviderDrawerState,
  drawerStateAtom,
  isInSessionWithSelectedClientAtom,
  patientHasCompletedMeasureResponsesFamily,
  patientShortIdToMeasuresFetchFailedAtom,
  patientShortIdWithFetchedMeasuresAtom,
  selectedClientInfoAtom,
  selectedClientMeasuresData,
  selectedPatientId,
  treatmentPlanContentStatus,
} from '../../state';
import { useGetFormsByPatientShortId } from '../../useGetFormsByPatientShortId';
import { ClientInformationProgressWrapper as ClientInformationProgress } from '../ClientInformationProgressWrapper';
import { completedFormsForSelectedClientAtom, hasNewFormsAtomFamily } from '../state';
import { useSendClientInfoTabViewTrackingEvent } from '../useSendClientInfoTabViewTrackingEvent';
import { TreatmentTab } from './TreatmentTab';

export function TreatmentTabWrapper() {
  const selectedClientInfo = useAtomValue(selectedClientInfoAtom);
  const patientShortId = selectedClientInfo?.patientShortId ?? '';
  const appointmentShortId = selectedClientInfo?.inSessionAppointmentShortId;
  const measureBundles = useAtomValue(selectedClientMeasuresData);

  const { loading: cssrsPromptLoading, result: cssrsPromptResult } = useShouldPromptForCSSRS({
    patientShortId,
    measureBundles,
    appointmentShortId,
  });

  useGetFormsByPatientShortId(patientShortId);
  const completedForms = useAtomValue(completedFormsForSelectedClientAtom);
  const nudges = selectedClientInfo?.nudges ?? [];
  const hasNewForms = useAtomValue(hasNewFormsAtomFamily(patientShortId));
  const patientId = useAtomValue(selectedPatientId) ?? '';
  const setDrawerState = useSetAtom(drawerStateAtom);
  const setTreatmentPlanContentStatus = useSetAtom(treatmentPlanContentStatus);
  const onClickTreatmentPlans = (treatmentPlanStatus: TreatmentPlanStatus) => {
    setTreatmentPlanContentStatus(treatmentPlanStatus);
    setDrawerState(ProviderDrawerState.TREATMENT_PLANS_CONTENT);
  };

  const hasCompletedForms = completedForms && Object.values(completedForms).length > 0;
  const shouldShowClinicalConsiderationSection = !!nudges.length;

  const isInSessionWithSelectedClient = useAtomValue(isInSessionWithSelectedClientAtom);
  const shouldShowFormsSection = isInSessionWithSelectedClient || hasCompletedForms;
  const measuresFetchedForPatient = useAtomValue(patientShortIdWithFetchedMeasuresAtom);
  const measuresFetchFailedForPatient = useAtomValue(patientShortIdToMeasuresFetchFailedAtom)[patientShortId];
  const hasCompletedMeasureResponses = useAtomValue(patientHasCompletedMeasureResponsesFamily(patientShortId));
  const hasMeasuresData = !!patientShortId && measuresFetchedForPatient === patientShortId;
  const isMeasuresDataReady = !!patientShortId && (hasMeasuresData || !!measuresFetchFailedForPatient);

  useSendClientInfoTabViewTrackingEvent(
    patientShortId,
    EventTypeEnum.TREATMENT_TAB,
    hasMeasuresData ? { hasCompletedMeasureResponses } : {},
    isMeasuresDataReady,
  );

  return (
    <TreatmentTab
      shouldShowAnytimeCSSRS={true}
      hasNewForms={hasNewForms}
      nudges={nudges}
      completedForms={completedForms}
      patientShortId={patientShortId}
      onClickTreatmentPlans={onClickTreatmentPlans}
      shouldShowFormsSection={shouldShowFormsSection}
      shouldShowClinicalConsiderationSection={shouldShowClinicalConsiderationSection}
      patientId={patientId}
      isInSessionWithSelectedClient={isInSessionWithSelectedClient}
      treatmentPlansComponent={TreatmentPlansOverviewConnected}
      clientInformationProgress={ClientInformationProgress}
      cssrsPromptLoading={cssrsPromptLoading}
      cssrsPromptResult={cssrsPromptResult}
    />
  );
}
